import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../contexts/AuthContext';
import ModalBase from '../ModalBase';
import { exportToExcel } from '../../helpers/exportCsv';
import { ORIGEM, Carregando, Faixa90, PillOrigem, QuemResolve, dataHora, dia, diaBR, num, useOrdenacao } from './comum';

// Tira a declaracao de parametros "(@p1 int, @p2 nvarchar(10))" do comeco: o
// que interessa e o comando.
const semParametros = (c) => String(c || '').replace(/^\s*\((@[^)]*(\([^)]*\))?[^)]*)+\)\s*/, '').trim();
const tipoBloqueio = (l) => ({ S: 'leitura', IS: 'leitura', X: 'gravação', IX: 'gravação', U: 'atualização', SIX: 'leitura + gravação' }[String(l || '').toUpperCase()] || l || '—');

function Ponta({ p, titulo }) {
  return (
    <div className={`ab-ponta ${p.vitima ? 'ab-ponta--vitima' : ''}`}>
      <div className="ab-ponta__topo">
        <strong>{titulo}</strong>
        <PillOrigem origem={p.dono} />
      </div>
      <p className="ab-ponta__sistema">{p.sistema}</p>
      <dl>
        <div><dt>Fazia</dt><dd>{p.faz}</dd></div>
        <div><dt>Bloqueio</dt><dd>{tipoBloqueio(p.lock)}</dd></div>
        <div><dt>Máquina</dt><dd>{p.host || '—'} · {p.programa || '—'}</dd></div>
        {p.objeto && p.objeto !== 'adhoc' && <div><dt>Procedure</dt><dd><code>{p.objeto}</code></dd></div>}
      </dl>
      <details className="ab-recolhido">
        <summary>Ver comando</summary>
        <pre className="ab-sql ab-sql--curto">{semParametros(p.comando) || '(sem comando)'}</pre>
      </details>
    </div>
  );
}

export function ModalDeadlock({ id, onClose }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  useEffect(() => {
    axios.get(`${API}/analise-banco/deadlocks/${id}`)
      .then(({ data }) => setDados(data.data))
      .catch((e) => setErro(e.response?.data?.message || 'Não foi possível abrir o deadlock.'));
  }, [id]);
  const d = dados?.deadlock;
  const vitimas = (dados?.processos || []).filter((p) => p.vitima);
  const vencedores = (dados?.processos || []).filter((p) => !p.vitima);
  const donos = new Set((dados?.processos || []).map((p) => p.dono));
  const acao = donos.has('portal') || donos.has('centrao_erp') ? 'nos' : 'micro';
  return (
    <ModalBase titulo={d ? `Deadlock · ${dataHora(d.ocorrido_em)}` : 'Deadlock'} onClose={onClose} largura={1000}>
      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}
      {!d && !erro && <Carregando />}
      {d && (
        <>
          <p className="ab-narrativa">{d.narrativa}</p>
          <div className="ab-numeros">
            <span>em <strong>{d.tabelas.map((t) => t.nome).join(' e ') || '—'}</strong></span>
            <span className="cell-muted">{d.objetos}</span>
            <span>quem corrige: <QuemResolve acao={acao} /></span>
          </div>
          <p className="cell-muted" style={{ fontSize: '0.82rem' }}>
            Dois processos travaram um esperando o outro. O SQL Server escolheu um deles e cancelou o comando: quem estava do outro lado do sistema recebeu erro.
          </p>
          <div className="ab-pontas">
            {vitimas.map((p, i) => <Ponta key={`v${i}`} p={p} titulo="✖ Cancelado" />)}
            {vencedores.map((p, i) => <Ponta key={`w${i}`} p={p} titulo="Seguiu" />)}
          </div>
        </>
      )}
    </ModalBase>
  );
}

function NomeTabela({ t }) {
  return (
    <span className="ab-tabela">
      <span className="cell-strong">{t.nome}</span>
      {t.tecnico && t.tecnico !== t.nome && <small className="cell-muted">{t.tecnico}</small>}
    </span>
  );
}

// Sistema nosso no deadlock = correcao nossa (os portais so leem o ERP).
const acaoDoGrupo = (g) => ((g.sistemasPorDono?.portal || []).length + (g.sistemasPorDono?.centrao_erp || []).length > 0 ? 'nos' : 'micro');

export default function Deadlocks({ filtroInicial, onFiltro, onAbrirProblema }) {
  const [visao, setVisao] = useState(filtroInicial?.objeto || filtroInicial?.dia ? 'lista' : 'problemas');
  const [dias, setDias] = useState(90);
  const [origem, setOrigem] = useState('');
  const [objeto, setObjeto] = useState(filtroInicial?.objeto || '');
  const [diaFiltro, setDiaFiltro] = useState(filtroInicial?.dia || '');
  const [linhas, setLinhas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState(null);

  useEffect(() => {
    setObjeto(filtroInicial?.objeto || '');
    setDiaFiltro(filtroInicial?.dia || '');
    if (filtroInicial?.objeto || filtroInicial?.dia) setVisao('lista');
  }, [filtroInicial?.objeto, filtroInicial?.dia]);

  // Os filtros vao para a URL (voltar do navegador funciona e o link e compartilhavel).
  useEffect(() => { onFiltro?.({ objeto: objeto || null, dia: diaFiltro || null }); }, [objeto, diaFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Clique rapido em dois filtros: so a ultima busca vale.
    let valida = true;
    setCarregando(true); setErro('');
    const params = { dias: diaFiltro ? 400 : dias, origem: origem || undefined, objeto: objeto || undefined, dia: diaFiltro || undefined };
    const rota = visao === 'problemas' ? 'deadlocks/grupos' : 'deadlocks';
    axios.get(`${API}/analise-banco/${rota}`, { params })
      .then(({ data }) => {
        if (!valida) return;
        if (visao === 'problemas') setGrupos(data.data || []); else setLinhas(data.data || []);
      })
      .catch((e) => { if (valida) setErro(e.response?.data?.message || 'Não foi possível carregar os deadlocks.'); })
      .finally(() => { if (valida) setCarregando(false); });
    return () => { valida = false; };
  }, [visao, dias, origem, objeto, diaFiltro]);

  const { ordenadas, Th } = useOrdenacao(linhas, null);
  const maxComum = Math.max(1, ...grupos.flatMap((g) => Object.values(g.porDia || {})));

  const exportar = () => (visao === 'problemas'
    ? exportToExcel(`deadlocks-por-problema-${new Date().toISOString().slice(0, 10)}`, [
      { label: 'Tabela', value: (g) => g.tabelas.map((t) => t.nome).join(' + ') },
      { label: 'Tabela (técnico)', value: (g) => g.chave },
      { label: 'Quem resolve', value: (g) => ({ micro: 'Microuniverso', nos: 'Nós (TI)', ambos: 'Os dois lados' }[acaoDoGrupo(g)]) },
      { label: 'Sistemas envolvidos', value: (g) => g.sistemas.map((s) => s.nome).join(' · ') },
      { label: 'Deadlocks', value: (g) => g.n },
      { label: 'Primeiro', value: (g) => dataHora(g.primeiro) },
      { label: 'Último', value: (g) => dataHora(g.ultimo) },
      { label: 'Pior dia', value: (g) => `${diaBR(g.pico.dia)} (${g.pico.n})` },
      { label: 'Horário', value: (g) => g.horarios?.texto || '' },
    ], grupos, 'Deadlocks por problema')
    : exportToExcel(`deadlocks-${new Date().toISOString().slice(0, 10)}`, [
      { label: 'Quando', value: (l) => dataHora(l.ocorrido_em) },
      { label: 'Origem', value: (l) => (ORIGEM[l.origem] || ORIGEM.outro).rotulo },
      { label: 'Tabela', value: (l) => l.tabelas.map((t) => t.nome).join(' + ') },
      { label: 'O que aconteceu', value: (l) => l.narrativa },
    ], ordenadas, 'Deadlocks'));

  return (
    <>
      <div className="filter-card" style={{ marginBottom: 14 }}>
        <div className="filter-card__field">
          <label>Ver</label>
          <div className="portal-form-actions" style={{ margin: 0 }}>
            <button type="button" onClick={() => setVisao('problemas')}
              className={`btn-portal ${visao === 'problemas' ? 'btn-portal--primary' : 'btn-portal--outline'}`}><i className="bi bi-collection" /> Por problema</button>
            <button type="button" onClick={() => setVisao('lista')}
              className={`btn-portal ${visao === 'lista' ? 'btn-portal--primary' : 'btn-portal--outline'}`}><i className="bi bi-list-ul" /> Um por um</button>
          </div>
        </div>
        {!diaFiltro && (
          <div className="filter-card__field">
            <label>Período</label>
            <div className="portal-form-actions" style={{ margin: 0 }}>
              {[7, 30, 90, 400].map((d) => (
                <button key={d} type="button" onClick={() => setDias(d)}
                  className={`btn-portal ${dias === d ? 'btn-portal--primary' : 'btn-portal--outline'}`}>{d === 400 ? 'Tudo' : `${d} dias`}</button>
              ))}
            </div>
          </div>
        )}
        <div className="filter-card__field">
          <label>Origem</label>
          <div className="ab-chips">
            <button type="button" className={`vw-chip ${!origem ? 'vw-chip--on' : 'vw-chip--off'}`} onClick={() => setOrigem('')}>Todas</button>
            {['erp', 'portal', 'misto'].map((o) => (
              <button key={o} type="button" className={`vw-chip ${origem === o ? 'vw-chip--on' : 'vw-chip--off'}`}
                style={origem === o ? { '--cor': ORIGEM[o].cor } : undefined} onClick={() => setOrigem(o)}>
                <span className="vw-chip__cor" style={{ background: ORIGEM[o].cor }} />{ORIGEM[o].rotulo}
              </button>
            ))}
          </div>
        </div>
        {(objeto || diaFiltro) && (
          <div className="filter-card__field">
            <label>Filtrando por</label>
            <div className="ab-chips">
              {diaFiltro && <button type="button" className="vw-chip vw-chip--on" onClick={() => setDiaFiltro('')} title="Tirar o filtro de dia">dia {diaBR(diaFiltro)}/{diaFiltro.slice(0, 4)} <i className="bi bi-x" /></button>}
              {objeto && <button type="button" className="vw-chip vw-chip--on" onClick={() => setObjeto('')} title="Tirar o filtro de tabela">{objeto} <i className="bi bi-x" /></button>}
            </div>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: 8 }}>
        <span className="cell-muted">
          {carregando ? 'Carregando…' : visao === 'problemas'
            ? `${num(grupos.length)} problemas (deadlocks agrupados pela tabela em disputa) · faixas na mesma escala · clique para abrir`
            : `${num(linhas.length)} deadlocks · clique para ver quem travou quem`}
        </span>
        <button type="button" className="btn-portal btn-portal--outline" onClick={exportar} disabled={carregando || (visao === 'problemas' ? !grupos.length : !linhas.length)}>
          <i className="bi bi-file-earmark-excel" /> Excel
        </button>
      </div>

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}
      {carregando ? <Carregando texto="Carregando deadlocks..." /> : visao === 'problemas' ? (
        <div className="table-shell">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Onde (tabela em disputa)</th>
                <th>Quem resolve</th>
                <th>Sistemas envolvidos</th>
                <th style={{ textAlign: 'right' }}>Deadlocks</th>
                <th>Quando</th>
                <th style={{ width: 190 }}>Últimos 90 dias</th>
              </tr>
            </thead>
            <tbody>
              {grupos.map((g) => (
                <tr key={g.chave} onClick={() => (onAbrirProblema ? onAbrirProblema({ id: `deadlock:${g.chave}`, fallback: () => { setObjeto(g.chave); setVisao('lista'); } }) : (setObjeto(g.chave), setVisao('lista')))} style={{ cursor: 'pointer' }}>
                  <td className="ab-col-tabela">{g.tabelas.length ? g.tabelas.map((t) => <NomeTabela key={t.tecnico} t={t} />) : '—'}</td>
                  <td><QuemResolve acao={acaoDoGrupo(g)} /></td>
                  <td><ul className="ab-sistemas">{g.sistemas.slice(0, 3).map((s) => <li key={s.nome}>{s.nome}</li>)}</ul></td>
                  <td style={{ textAlign: 'right' }}><span className="cell-strong ab-grande">{num(g.n)}</span></td>
                  <td className="cell-muted" style={{ whiteSpace: 'nowrap' }}>
                    {dia(g.primeiro) === dia(g.ultimo) ? `em ${dia(g.primeiro)}` : `${dia(g.primeiro)} a ${dia(g.ultimo)}`}
                    {g.pico.n > 1 && g.dias > 1 && <><br />pior dia {diaBR(g.pico.dia)} ({num(g.pico.n)})</>}
                    {g.horarios?.texto && <><br />mais às {g.horarios.texto}</>}
                  </td>
                  <td><Faixa90 porDia={g.porDia} max={maxComum} cor={ORIGEM[g.dono]?.cor} /></td>
                </tr>
              ))}
              {!grupos.length && <tr><td colSpan={6} className="cell-muted">Nenhum deadlock com esse filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-shell">
          <table className="portal-table">
            <thead>
              <tr>
                <Th campo="ocorrido_em" valor={(l) => new Date(l.ocorrido_em).getTime()}>Quando</Th>
                <Th campo="origem">Origem</Th>
                <Th campo="objetos">Onde (tabela)</Th>
                <Th campo="narrativa">O que aconteceu</Th>
              </tr>
            </thead>
            <tbody>
              {ordenadas.map((l) => (
                <tr key={l.id} onClick={() => setAberto(l.id)} style={{ cursor: 'pointer' }}>
                  <td style={{ whiteSpace: 'nowrap' }}>{dataHora(l.ocorrido_em)}</td>
                  <td><PillOrigem origem={l.origem} /></td>
                  <td className="ab-col-tabela">{l.tabelas.length ? l.tabelas.map((t) => <NomeTabela key={t.tecnico} t={t} />) : '—'}</td>
                  <td className="ab-quebra">{l.narrativa}</td>
                </tr>
              ))}
              {!ordenadas.length && <tr><td colSpan={4} className="cell-muted">Nenhum deadlock com esse filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {aberto && <ModalDeadlock id={aberto} onClose={() => setAberto(null)} />}
    </>
  );
}
